package fyi.moko.hotpl.domain.usecase

import fyi.moko.hotpl.data.image.PlaceImageStore
import fyi.moko.hotpl.domain.model.PhotoGroup
import fyi.moko.hotpl.domain.model.PhotoKind
import fyi.moko.hotpl.domain.model.PhotoRef
import fyi.moko.hotpl.domain.repository.PlaceRepository

/**
 * 사용자가 방문을 기록하며 올린 사진 한 장. [group]/[kind]는 분류기가 채우고 사용자가 화면에서
 * 고칠 수 있다 — 여기 도착할 때는 이미 정해진 값이다.
 */
data class VisitPhotoDraft(
    val bytes: ByteArray,
    val kind: PhotoKind? = null,
    val group: PhotoGroup? = null,
) {
    // ByteArray는 참조 비교라 data class의 기본 equals가 쓸모없다. 목록 비교가 필요한 곳이
    // 있어 내용 비교로 바꾼다.
    override fun equals(other: Any?): Boolean =
        this === other ||
            (other is VisitPhotoDraft && bytes.contentEquals(other.bytes) && kind == other.kind && group == other.group)

    override fun hashCode(): Int = 31 * (31 * bytes.contentHashCode() + (kind?.hashCode() ?: 0)) + (group?.hashCode() ?: 0)
}

/**
 * 방문을 기록하면서 올린 사진들을 **가게 갤러리와 그 방문 기록으로 갈라** 저장한다.
 *
 * ── 왜 나누는가 ──────────────────────────────────────────
 * 사용자는 가게에서 사진 몇 장을 한 번에 찍는데 그 안에 간판·메뉴판·음식이 섞여 있다. 전부
 * 방문에만 붙이면 가게 갤러리가 비고, 전부 가게에 붙이면 그날 먹은 것이 방문 기록에서 사라진다.
 * 화면이 실제로 갈라야 하는 축이 이거라 분류기도 이 축을 가장 잘 맞힌다
 * (VersionSandbox `docs/photo_classify_probe.md`: 37장 중 36장).
 *
 * 어느 쪽인지 못 정한 사진은 **방문 쪽으로 보낸다.** 사진은 이 방문 중에 찍혔다는 것만은
 * 확실하고, 잘못 넣었을 때 가게 갤러리를 어지럽히는 쪽이 되돌리기 더 번거롭기 때문이다.
 * 사용자는 어느 쪽이든 나중에 옮길 수 있다.
 */
class AddVisitWithPhotosUseCase(
    private val repository: PlaceRepository,
    private val imageStore: PlaceImageStore,
) {

    suspend operator fun invoke(
        placeId: String,
        visitedAt: Long,
        note: String? = null,
        emotion: String? = null,
        photos: List<VisitPhotoDraft> = emptyList(),
    ): String {
        val saved = photos.map { draft ->
            val fileName = imageStore.save(draft.bytes)
            draft to PhotoRef(fileName = fileName, kind = draft.kind, group = draft.group)
        }

        val (placePhotos, visitPhotos) = saved.partition { (draft, _) ->
            (draft.kind?.group ?: draft.group) == PhotoGroup.PLACE
        }

        if (placePhotos.isNotEmpty()) {
            // 가게 사진은 기존 목록 뒤에 붙인다. 앞에 넣으면 대표 사진이 방문할 때마다 바뀌어
            // 목록 화면이 흔들린다 — 대표는 순서가 아니라 종류로 정해지지만, 같은 종류끼리는
            // 먼저 들어온 것이 남는 편이 예측 가능하다.
            repository.getPlace(placeId)?.let { place ->
                repository.savePlace(place.copy(photos = place.photos + placePhotos.map { it.second }))
            }
        }

        return repository.addVisit(
            placeId = placeId,
            visitedAt = visitedAt,
            note = note,
            emotion = emotion,
            photos = visitPhotos.map { it.second },
        )
    }
}
